import React from 'react';
import { Box, Typography } from '@mui/material';
import { type LiveRankingItem, type LiveRankingsCategory } from '../api/liveRankings';
import { LiveRow, LIVE_ROW_BG } from './LiveRankings';
import { RankingLoadingSkeleton } from './rankings/RankingPageLayout';

/** Max rows rendered in the download/share PNG asset (1080×1350). */
export const LIVE_ASSET_MAX_RANKINGS = 8;
export const LIVE_ASSET_WIDTH_PX = 1080;
export const LIVE_ASSET_HEIGHT_PX = 1350;
export const LIVE_ASSET_PADDING_TOP_PX = 96;
export const LIVE_ASSET_PADDING_X_PX = 48;
export const LIVE_ASSET_PADDING_BOTTOM_PX = 48;
/** Subtitle block → period pill (original section gap). */
export const LIVE_ASSET_HEADER_TO_PILL_GAP_PX = 48;
/** Period pill → rankings list (original section gap). */
export const LIVE_ASSET_PILL_TO_CONTENT_GAP_PX = 48;

/** Vertical gap between export ranking rows (matches LiveRow list in LiveRankings.tsx). */
const ASSET_RANKING_ROW_GAP_PX = 8;

export function parseCategoryFromSearch(search: string): LiveRankingsCategory | undefined {
    const params = new URLSearchParams(search);
    const raw = params.get('category');
    if (raw === 'tv' || raw === 'radio' || raw === 'digital') return raw;
    return undefined;
}

export function liveRankingsCategoryDisplayLabel(
    category: LiveRankingsCategory | undefined
): string {
    switch (category) {
        case 'digital':
            return 'Digital';
        case 'tv':
            return 'TV';
        case 'radio':
            return 'Radio';
        default:
            return 'Todas';
    }
}

export function liveRankingsCategoryCaption(category: LiveRankingsCategory | undefined): string {
    return `Categoría: ${liveRankingsCategoryDisplayLabel(category)}`;
}

export interface LiveRankingCardProps {
    id?: string;
    rankings: LiveRankingItem[];
    updatedAtLabel: string;
    /** Active list filter; shown top-left on export asset (Todas when omitted). */
    category?: LiveRankingsCategory;
    isLoading?: boolean;
    isReady?: boolean;
    /** End instant for on-air duration (rankings snapshot `updatedAt`). */
    referenceMs: number | null;
}

export const LiveRankingCard: React.FC<LiveRankingCardProps> = ({
    id,
    rankings,
    updatedAtLabel,
    category,
    isLoading = false,
    isReady = false,
    referenceMs,
}) => {
    const assetRankings = rankings.slice(0, LIVE_ASSET_MAX_RANKINGS);

    return (
        <Box
            id={id}
            data-ranking-card
            data-ready={isReady ? 'true' : undefined}
            sx={{
                width: LIVE_ASSET_WIDTH_PX,
                height: LIVE_ASSET_HEIGHT_PX,
                minHeight: LIVE_ASSET_HEIGHT_PX,
                maxHeight: LIVE_ASSET_HEIGHT_PX,
                overflow: 'hidden',
                bgcolor: '#1a1a1a',
                color: '#fff',
                pt: `${LIVE_ASSET_PADDING_TOP_PX}px`,
                px: `${LIVE_ASSET_PADDING_X_PX}px`,
                pb: `${LIVE_ASSET_PADDING_BOTTOM_PX}px`,
                position: 'relative',
                boxSizing: 'border-box',
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gridTemplateColumns: '1fr',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 40,
                    left: 40,
                    zIndex: 2,
                    pointerEvents: 'none',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 22,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        color: '#fff',
                        border: '2px solid #fff',
                        borderRadius: '20px',
                        px: '18px',
                        py: '8px',
                        lineHeight: 1.2,
                    }}
                >
                    {liveRankingsCategoryCaption(category)}
                </Typography>
            </Box>

            {/* Background image */}
            <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                <Box
                    component="img"
                    alt=""
                    src="/ranking-bg.png"
                    crossOrigin="anonymous"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.05)' }} />
            </Box>

            {/* Main content — grid row 1; padding-based gaps for reliable PNG capture */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Header cluster: title + period pill (non-shrinking) */}
                <Box sx={{ flexShrink: 0, width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <Typography
                            align="center"
                            sx={{
                                fontFamily: '"DM Serif Display", serif',
                                fontSize: 64,
                                lineHeight: '64px',
                                color: '#fff',
                            }}
                        >
                            Podscope Top {LIVE_ASSET_MAX_RANKINGS}
                        </Typography>
                        <Typography
                            align="center"
                            sx={{
                                fontFamily: '"DM Serif Display", serif',
                                fontSize: 52,
                                lineHeight: '52px',
                                color: '#fff',
                            }}
                        >
                            Livestreams en YouTube
                        </Typography>
                    </Box>

                    <Box
                        data-live-asset-period-pill
                        sx={{
                            pt: `${LIVE_ASSET_HEADER_TO_PILL_GAP_PX}px`,
                            display: 'flex',
                            justifyContent: 'center',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                px: '18px',
                                py: '8px',
                                borderRadius: '30px',
                                border: '2px solid #fff',
                                bgcolor: 'transparent',
                            }}
                        >
                            <Typography
                                align="center"
                                sx={{
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: 20,
                                    fontWeight: 600,
                                    lineHeight: 1.35,
                                    color: '#fff',
                                    maxWidth: 920,
                                    px: 1,
                                }}
                            >
                                {updatedAtLabel}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Rankings list */}
                <Box
                    data-live-asset-rankings
                    sx={{
                        px: '72px',
                        pt: `${LIVE_ASSET_PILL_TO_CONTENT_GAP_PX}px`,
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    {isLoading && (
                        <RankingLoadingSkeleton
                            count={LIVE_ASSET_MAX_RANKINGS}
                            backgroundColor={LIVE_ROW_BG}
                        />
                    )}

                    {!isLoading && assetRankings.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                color: 'rgba(255,255,255,0.5)',
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '1rem',
                            }}
                        >
                            No hay transmisiones en vivo en este momento
                        </Box>
                    )}

                    {!isLoading && assetRankings.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: `${ASSET_RANKING_ROW_GAP_PX}px`,
                                '& > * > a > div': { marginBottom: '0 !important' },
                            }}
                        >
                            {assetRankings.map(item => (
                                <LiveRow
                                    key={item.videoId ?? item.channelName}
                                    item={item}
                                    staticSize
                                    referenceMs={referenceMs}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Footer — grid row 2; always at bottom of 1350px asset */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    gap: 2,
                    width: '100%',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 14,
                        lineHeight: '14px',
                        color: '#fff',
                        width: 460,
                        maxWidth: '100%',
                    }}
                >
                    Dato: espectadores en vivo a la hora seleccionada
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"DM Serif Display", serif',
                            fontSize: 28,
                            lineHeight: '28px',
                            color: '#fff',
                        }}
                    >
                        En vivo en podscope.co/live
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
